import React, { useState } from 'react';
import { AlertTriangle, Anchor, Users, Activity, FileText, Info } from 'lucide-react';
import PilotOperationsTab from './PilotOperationsTab';
import TowingOperationsTab from './TowingOperationsTab';
import DivingOperationsTab from './DivingOperationsTab';
import { vesselTypes } from '../../../../config/vesselTypeRequirements';

/**
 * Special Operations Tab - Shows vessel-specific operations based on type
 */
const SpecialOperationsTab = ({ currentVessel, vesselSettings, onAddEntry }) => {
  const [activeOperation, setActiveOperation] = useState(null);
  
  // Use the SAME logic as LogbookTabCompact which we know works!
  const vesselType = currentVessel?.type || 'pax';
  const vesselName = currentVessel?.name || 'Vessel';
  const grossTonnage = parseInt(currentVessel?.grossTonnage) || 0;
  const operatingArea = currentVessel?.operatingStatus || 'seagoing';
  const waterCategory = currentVessel?.category?.replace('cat', '') || '5';
  
  // Get vessel type label from configuration
  const vesselInfo = vesselTypes[vesselType];
  const vesselTypeLabel = vesselInfo?.label || vesselType;
  
  // Operations come from vesselSettings (from the Operations tab in vessel settings)
  const operations = vesselSettings?.operations || [];
  
  // Determine which operations are available based on vessel type and configuration
  const availableOperations = [];
  
  // Check by vessel type first
  if (vesselType === 'pilot') {
    availableOperations.push({
      id: 'pilot',
      label: 'Pilot Transfer Operations',
      icon: Users,
      color: 'blue',
      description: 'Record pilot embarkation/disembarkation and ladder arrangements'
    });
  }
  
  if (vesselType === 'tug') {
    availableOperations.push({
      id: 'towing',
      label: 'Towing Operations',
      icon: Anchor,
      color: 'indigo',
      description: 'Log towing operations, bollard pull, and towline configurations'
    });
  }
  
  if (vesselType === 'dive_support') {
    availableOperations.push({
      id: 'diving',
      label: 'Diving Operations',
      icon: Activity,
      color: 'cyan',
      description: 'Track diving operations, decompression schedules, and equipment'
    });
  }
  
  // Also check operations array for vessels with multiple capabilities
  if (operations.includes('pilot_transfer') && !availableOperations.find(op => op.id === 'pilot')) {
    availableOperations.push({
      id: 'pilot',
      label: 'Pilot Transfer Operations',
      icon: Users,
      color: 'blue',
      description: 'Record pilot embarkation/disembarkation and ladder arrangements'
    });
  }
  
  if (operations.includes('towing_operations') && !availableOperations.find(op => op.id === 'towing')) {
    availableOperations.push({
      id: 'towing',
      label: 'Towing Operations',
      icon: Anchor,
      color: 'indigo',
      description: 'Log towing operations, bollard pull, and towline configurations'
    });
  }
  
  if (operations.includes('dive_support') && !availableOperations.find(op => op.id === 'diving')) {
    availableOperations.push({
      id: 'diving',
      label: 'Diving Operations',
      icon: Activity,
      color: 'cyan',
      description: 'Track diving operations, decompression schedules, and equipment'
    });
  }
  
  // Add vessel type specific forms that aren't full operations
  const specialForms = [];
  
  if (vesselType === 'pax' || vesselType === 'trip_boat') {
    specialForms.push({
      id: 'passenger-manifest',
      label: 'Passenger Manifest',
      description: 'Record passenger counts and safety briefings'
    });
  }
  
  if (vesselType === 'ctv') {
    specialForms.push({
      id: 'crew-transfer',
      label: 'Crew Transfer Log',
      description: 'Record personnel transfers to offshore installations'
    });
  }
  
  if (vesselType === 'police' || vesselType === 'patrol') {
    specialForms.push({
      id: 'incident-report',
      label: 'Incident Report',
      description: 'Document patrol activities and incidents'
    });
  }
  
  // Set default active operation if only one is available
  if (!activeOperation && availableOperations.length === 1) {
    setActiveOperation(availableOperations[0].id);
  }
  
  // If there's only one operation, show it directly
  if (availableOperations.length === 1 && activeOperation === availableOperations[0].id) {
    const op = availableOperations[0];
    if (op.id === 'pilot') {
      return <PilotOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />;
    }
    if (op.id === 'towing') {
      return <TowingOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />;
    }
    if (op.id === 'diving') {
      return <DivingOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />;
    }
  }
  
  // If multiple operations or none, show selection interface
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <FileText className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Special Operations</h2>
                <p className="text-sm text-gray-600">
                  {vesselName} - {vesselTypeLabel}
                  {grossTonnage > 0 && ` - ${grossTonnage} GT`}
                  {` - Cat ${waterCategory}`}
                </p>
              </div>
            </div>
            {availableOperations.length > 1 && activeOperation && (
              <button
                onClick={() => setActiveOperation(null)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to Operations
              </button>
            )}
          </div>
        </div>
        
        {/* Operation Selection or Content */}
        {availableOperations.length === 0 ? (
          // No operations available
          <div className="p-6">
            <div className="text-center py-12">
              <AlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Special Operations Available
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                This vessel type ({vesselType || 'standard'}) doesn't have specific operational modules.
              </p>
              
              {specialForms.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Available Forms:</p>
                  <div className="space-y-2 max-w-md mx-auto">
                    {specialForms.map(form => (
                      <div key={form.id} className="p-3 bg-gray-50 rounded-lg text-left">
                        <p className="font-medium text-sm text-gray-900">{form.label}</p>
                        <p className="text-xs text-gray-600 mt-1">{form.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-8 bg-cyan-50 rounded-lg p-4 max-w-lg mx-auto text-left">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium mb-1">Configure Operations:</p>
                    <p>Go to Settings → Vessel Settings → Operations tab to enable special operations like towing, pilot transfer, or diving support.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : availableOperations.length > 1 && !activeOperation ? (
          // Multiple operations - show selection grid
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">Select an operation type to manage:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableOperations.map(op => {
                const Icon = op.icon;
                return (
                  <button
                    key={op.id}
                    onClick={() => setActiveOperation(op.id)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-cyan-300 hover:bg-cyan-50 transition-colors text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 bg-${op.color}-100 rounded-lg`}>
                        <Icon className={`h-6 w-6 text-${op.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{op.label}</h3>
                        <p className="text-xs text-gray-600 mt-1">{op.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {specialForms.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm font-medium text-gray-700 mb-3">Quick Forms:</p>
                <div className="flex flex-wrap gap-2">
                  {specialForms.map(form => (
                    <button
                      key={form.id}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      onClick={() => {/* Handle form */}}
                    >
                      {form.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      
      {/* Show selected operation content */}
      {activeOperation && availableOperations.length > 1 && (
        <div>
          {activeOperation === 'pilot' && (
            <PilotOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />
          )}
          {activeOperation === 'towing' && (
            <TowingOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />
          )}
          {activeOperation === 'diving' && (
            <DivingOperationsTab currentVessel={currentVessel} onAddEntry={onAddEntry} />
          )}
        </div>
      )}
    </div>
  );
};

export default SpecialOperationsTab;